/**
 * Server refusals the customer must not read in English (ops walk B8).
 *
 * The backend validates request bodies with class-validator, whose messages
 * are fixed English sentences about field names: "email must be an email",
 * "password must be longer than or equal to 8 characters", "property foo
 * should not exist". They used to reach the register page, the canvas and
 * map editors and the upload box as they came. The backend now marks such a
 * refusal `code: "VALIDATION_FAILED"` and lists the rules in `messages`;
 * older answers only have `message`, an array or the rules joined with ", ".
 *
 * Each known rule becomes the shop's own translated copy (the same
 * `errorMessages.*` keys the forms use); any other class-validator rule
 * becomes a generic "check what you entered". A sentence the server already
 * wrote for people (coupon refusals, which it localizes) is kept as is.
 */

type Translate = (key: string, fallback: string) => string;

/** class-validator's default message shapes: "<property> must …", "… should not exist". */
const VALIDATION_RULE =
  /^(?:property \S+ should not exist|(?:each value in )?[A-Za-z_][\w.[\]]* (?:must|should) .+)$/;

/** Split a joined refusal at the start of each rule, not at commas inside one. */
function splitJoined(text: string): string[] {
  return text
    .split(/, (?=(?:property \S+ should not exist|(?:each value in )?[A-Za-z_][\w.[\]]* (?:must|should) ))/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** The server's refusal as a list of messages; [] when it said nothing usable. */
export function serverMessagesOf(err: unknown): string[] {
  const data = (err as { response?: { data?: { message?: unknown; messages?: unknown } } } | null)
    ?.response?.data;
  const list = Array.isArray(data?.messages) ? data?.messages : data?.message;
  if (Array.isArray(list)) return list.map(String).filter((s) => s.trim() !== "");
  if (typeof list === "string" && list.trim()) return [list];
  return [];
}

/** True when the text is class-validator's English (one rule or several joined). */
export function isServerValidationText(text: string): boolean {
  const parts = splitJoined(text || "");
  return parts.length > 0 && parts.every((p) => VALIDATION_RULE.test(p));
}

/** Backend sentences that are English but not class-validator's. */
const KNOWN: Record<string, [string, string]> = {
  "Email already exists.": [
    "errorMessages.emailTaken",
    "This email address is already registered. Sign in or reset your password.",
  ],
  "Passwords do not match.": ["errorMessages.confirmPassword", "Passwords do not match"],
  "Invalid or expired reset token.": [
    "errorMessages.invalidLink",
    "This link is invalid or has expired. Request a new one.",
  ],
  "Invalid verification token.": [
    "errorMessages.invalidLink",
    "This link is invalid or has expired. Request a new one.",
  ],
};

function ruleCopy(rule: string): [string, string] {
  if (/ must be an email$/.test(rule)) return ["errorMessages.email", "Invalid email address"];
  if (/^password must be longer than or equal to \d+ characters$/i.test(rule))
    return ["errorMessages.password", "Password must be at least 8 characters"];
  if (/ must be longer than or equal to \d+ characters$/.test(rule))
    return ["errorMessages.tooShort", "This field is too short"];
  if (/ must be shorter than or equal to \d+ characters$/.test(rule))
    return ["errorMessages.tooLong", "This field is too long"];
  if (/ should not be empty$/.test(rule)) return ["errorMessages.required", "This field is required"];
  return [
    "errorMessages.invalidInput",
    "Some of the details are not valid. Please check them and try again.",
  ];
}

/**
 * The refusal in the customer's language, or null when there is nothing to
 * show (the caller then uses its own translated fallback).
 */
export function localizeServerMessage(
  raw: string | string[] | null | undefined,
  t: Translate,
): string | null {
  const list = (Array.isArray(raw) ? raw : raw ? [raw] : [])
    .map((s) => String(s).trim())
    .filter(Boolean);
  if (list.length === 0) return null;
  const out: string[] = [];
  for (const message of list) {
    const known = KNOWN[message];
    if (known) {
      out.push(t(known[0], known[1]));
      continue;
    }
    if (!isServerValidationText(message)) {
      out.push(message);
      continue;
    }
    for (const rule of splitJoined(message)) out.push(t(...ruleCopy(rule)));
  }
  return [...new Set(out)].join(" ");
}
